/**
 * Prospection cote interface (conception 7) : compteurs du badge et de
 * l'accueil, titre de l'onglet, textes de la journee, echeances lisibles,
 * report d'une relance, lecture de l'adresse de l'ecran Relances.
 *
 * Les calculs de jour passent par le calendrier de Paris de @leads/shared,
 * comme l'API ; les heures affichees restent celles du navigateur, comme
 * partout dans l'application (identiques en France).
 */
import {
  HORIZONS_PROSPECTION, LIBELLES_INTERACTION, LIBELLES_RESULTAT, aHeureParis, ajouterJoursParis, bornesJourParis, partiesParis,
} from '@leads/shared';
import type {
  CompteursProspectionDTO, InteractionDTO, ProchaineEtapeDTO, RequeteRelancesProspection, ResumeFileAppelsDTO,
} from '@leads/shared';
import { extraitDeNotes, formaterDate, formaterHeure, formaterJourCivilLong, formaterNombre, pluriel } from './format';
import { HEURE_PAR_DEFAUT, libelleEcheance } from './prochaine-etape';
import type { BrouillonProchaineEtape } from './prochaine-etape';

/* ------------------------------ Compteurs --------------------------------- */

/** Badge « Relances » (conception 7.5) : les retards et les relances du jour. */
export function totalRelances(compteurs: CompteursProspectionDTO | undefined): number {
  return compteurs ? compteurs.enRetard + compteurs.aujourdhui : 0;
}

const PREFIXE_TITRE = /^\(\d+\) /;

/** « (11) Titre » ; un prefixe deja pose est remplace, jamais empile. */
export function titreAvecCompteur(titre: string, nombre: number): string {
  const nu = titre.replace(PREFIXE_TITRE, '');
  return nombre > 0 ? `(${nombre}) ${nu}` : nu;
}

function dontEnRetard(compteurs: CompteursProspectionDTO): string {
  return compteurs.enRetard > 0 ? `, dont ${formaterNombre(compteurs.enRetard)} en retard` : '';
}

/** Message de la premiere ouverture du jour. */
export function messageDuJour(compteurs: CompteursProspectionDTO): string {
  return `Vous avez ${pluriel(totalRelances(compteurs), 'relance')} aujourd'hui${dontEnRetard(compteurs)}.`;
}

/** « 11 relances prevues, dont 3 en retard » : ligne de date de l'accueil. */
export function resumeJournee(compteurs: CompteursProspectionDTO): string {
  const total = totalRelances(compteurs);
  if (total === 0) return 'Aucune relance prévue';
  return `${pluriel(total, 'relance prévue', 'relances prévues')}${dontEnRetard(compteurs)}`;
}

/** Premier mot du nom : « Julie » pour « Julie Moreau ». */
export function prenomDe(nom: string): string {
  return nom.trim().split(/\s+/)[0] ?? '';
}

/* ------------------------------- Relances --------------------------------- */

export type TonEcheance = 'danger' | 'accent' | 'neutre';

/** Pastille d'une ligne : l'heure pour aujourd'hui, le retard en rouge, sinon la date. */
export function pastilleEcheance(
  etape: Pick<ProchaineEtapeDTO, 'date' | 'horizon'>,
  maintenant: Date = new Date(),
): { texte: string; ton: TonEcheance } {
  if (etape.horizon === 'jour') return { texte: formaterHeure(etape.date), ton: 'accent' };
  return { texte: libelleEcheance(etape.date, maintenant), ton: etape.horizon === 'retard' ? 'danger' : 'neutre' };
}

/** « Appel · Rappeler la gerante » ; sans type (saisie d'avant le typage), « Relance ». */
export function motifRelance(etape: Pick<ProchaineEtapeDTO, 'type' | 'note'>): string {
  return [etape.type ? LIBELLES_INTERACTION[etape.type] : 'Relance', etape.note?.trim()].filter(Boolean).join(' · ');
}

/**
 * Report d'une relance (conception 7.3) : `jours` jours civils plus tard, a la
 * meme heure murale de Paris. Une relance en retard part d'aujourd'hui : la
 * reporter d'un jour depuis son echeance la laisserait en retard.
 */
export function dateDeReport(echeance: string, jours: number, maintenant: Date = new Date()): Date {
  const date = new Date(echeance);
  if (date >= bornesJourParis(maintenant).debut) return ajouterJoursParis(date, jours);
  const { heure, minute } = partiesParis(date);
  return ajouterJoursParis(aHeureParis(maintenant, heure, minute), jours);
}

/* --------------------------- Session d'appels ----------------------------- */

/** Bandeau « Demarrer ma session » de l'accueil (conception 7.2). */
export function texteBandeauSession(resume: ResumeFileAppelsDTO): { titre: string; detail: string } {
  const absents =
    resume.sansTelephone > 0
      ? ` ${pluriel(resume.sansTelephone, "lead sans téléphone n'y figure pas", "leads sans téléphone n'y figurent pas")}.`
      : '';
  if (resume.taille === 0) {
    return {
      titre: 'Aucun appel à passer pour le moment',
      detail: `Les relances d'appel du jour et les nouveaux leads à contacter apparaîtront ici.${absents}`,
    };
  }
  let scripts = '';
  if (resume.avecScriptPret >= resume.taille) scripts = resume.taille > 1 ? ', scripts IA prêts pour tous' : ', script IA prêt';
  else if (resume.avecScriptPret > 0) {
    const prets = resume.avecScriptPret > 1 ? 'scripts IA prêts' : 'script IA prêt';
    scripts = `, ${prets} pour ${formaterNombre(resume.avecScriptPret)} d'entre eux`;
  }
  return {
    titre: `${pluriel(resume.taille, 'appel')} à passer${scripts}`,
    // La file compte les retards avec ceux du jour : le detail les nomme.
    detail: `Vos relances d'appel en retard et du jour, puis des leads jamais contactés.${absents}`,
  };
}

/**
 * Proposition de la session : « Appel, vendredi 2 octobre a 10:00 ». Date videe pendant l'edition : la relance ne
 * partira pas sans (la fiche le signale) ; heure videe : elle partira a l'heure par defaut, que le resume dit.
 */
export function resumeProchaineEtape(brouillon: BrouillonProchaineEtape): string {
  const type = LIBELLES_INTERACTION[brouillon.type];
  if (!brouillon.date) return `${type}, date à choisir`;
  return `${type}, ${formaterJourCivilLong(brouillon.date).toLowerCase()} à ${brouillon.heure || HEURE_PAR_DEFAUT}`;
}

/** Dernier echange d'un lead de la file, en une ligne. */
export function texteDernierEchange(echange: InteractionDTO | null): string {
  if (!echange) return 'Aucun échange pour le moment : premier contact.';
  const quoi = echange.resultat ? LIBELLES_RESULTAT[echange.resultat] : LIBELLES_INTERACTION[echange.type];
  const notes = echange.notes ? extraitDeNotes(echange.notes) : '';
  return `${quoi}, le ${formaterDate(echange.date)}${notes ? ` — « ${notes} »` : ''}`;
}

/* ---------------------------- Ecran Relances ------------------------------ */

export const TAILLES_PAGE_RELANCES = [25, 50, 100] as const;

/**
 * Requete de l'ecran Relances lue dans l'adresse ; une valeur inconnue prend
 * sa valeur par defaut. `proprietaireId` n'est lu qu'avec `stats.equipe`, et
 * jamais pour l'appelant (`moiId`) : ses propres relances sont la vue par
 * defaut, celle d'aucun commercial designe.
 */
export function requeteRelancesDepuisUrl(recherche: URLSearchParams, vueEquipe: boolean, moiId?: string): RequeteRelancesProspection {
  const page = Number(recherche.get('page'));
  const taillePage = Number(recherche.get('taillePage'));
  const proprietaireId = vueEquipe ? recherche.get('proprietaireId') : null;
  return {
    horizon: HORIZONS_PROSPECTION.find((horizon) => horizon === recherche.get('horizon')) ?? 'retard',
    page: Number.isInteger(page) && page > 1 ? page : 1,
    taillePage: TAILLES_PAGE_RELANCES.find((taille) => taille === taillePage) ?? TAILLES_PAGE_RELANCES[0],
    ...(proprietaireId && proprietaireId !== moiId ? { proprietaireId } : {}),
  };
}
